import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { fetchBranches, fetchSubjects } from "@/api/branches";
import { createTeacher, fetchTeacherRates, setTeacherGroupRate, updateTeacher } from "@/api/teachers";
import { extractErrorMessage } from "@/types/common";
import { formatPercent } from "@/utils/format";
import type { Teacher } from "@/types/teacher";

interface TeacherFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Если передан — диалог работает в режиме редактирования существующего преподавателя. */
  teacher?: Teacher | null;
}

/** Строка таблицы «Процент по каждой группе» — переопределение базового процента (ТЗ §2.8). */
function GroupRateRow({ teacherId, groupId, groupCode, subjectName, studentsCount, percent }: { teacherId: string; groupId: string; groupCode: string; subjectName: string; studentsCount: number; percent: number | null }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(percent === null ? "" : String(percent));

  const mutation = useMutation({
    mutationFn: (next: number | null) => setTeacherGroupRate(teacherId, groupId, next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["teacher-rates", teacherId] }),
  });

  return (
    <TableRow>
      <TableCell>{groupCode}</TableCell>
      <TableCell>{subjectName}</TableCell>
      <TableCell align="right">{studentsCount}</TableCell>
      <TableCell width={140}>
        <TextField
          size="small"
          placeholder="базовый"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => mutation.mutate(value === "" ? null : Number(value))}
          inputProps={{ inputMode: "decimal" }}
        />
      </TableCell>
    </TableRow>
  );
}

export function TeacherFormDialog({ open, onClose, teacher }: TeacherFormDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(teacher);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [branchId, setBranchId] = useState("");
  const [subjectIds, setSubjectIds] = useState<string[]>([]);
  const [defaultRevenueSharePercent, setRevenueSharePercent] = useState(40);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });
  const { data: rates } = useQuery({
    queryKey: ["teacher-rates", teacher?.id],
    queryFn: () => fetchTeacherRates(teacher!.id),
    enabled: Boolean(teacher?.id) && open,
  });

  useEffect(() => {
    if (!open) return;
    setServerError(null);
    if (teacher) {
      setFullName(teacher.fullName);
      setPhone(teacher.phone);
      setBranchId(teacher.branchId);
      setSubjectIds(teacher.subjectIds);
      setRevenueSharePercent(teacher.defaultRevenueSharePercent);
      setLogin("");
      setPassword("");
    } else {
      setFullName("");
      setPhone("");
      setBranchId("");
      setSubjectIds([]);
      setRevenueSharePercent(40);
      setLogin("");
      setPassword("");
    }
  }, [open, teacher]);

  const mutation = useMutation({
    mutationFn: () =>
      isEdit
        ? updateTeacher(teacher!.id, { fullName, phone, subjectIds, defaultRevenueSharePercent })
        : createTeacher({ fullName, phone, branchId, subjectIds, defaultRevenueSharePercent, login, password }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["teachers"] });
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  const canSubmit = fullName.trim().length > 1 && phone.trim().length > 3 && subjectIds.length > 0 && (isEdit || (branchId && login && password));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Преподаватель — " + teacher!.fullName : "Добавить преподавателя"}</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField label="ФИО" fullWidth value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Телефон" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Филиал" fullWidth value={branchId} disabled={isEdit} onChange={(e) => setBranchId(e.target.value)}>
              {(branches ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={(subjects ?? []).map((s) => s.id)}
              getOptionLabel={(id) => (subjects ?? []).find((s) => s.id === id)?.name ?? id}
              value={subjectIds}
              onChange={(_, value) => setSubjectIds(value)}
              renderTags={(value, getTagProps) =>
                value.map((id, index) => <Chip label={(subjects ?? []).find((s) => s.id === id)?.name ?? id} size="small" {...getTagProps({ index })} key={id} />)
              }
              renderInput={(params) => <TextField {...params} label="Предметы" placeholder="Выберите предметы" />}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Базовый процент от прихода"
              type="number"
              fullWidth
              value={defaultRevenueSharePercent}
              onChange={(e) => setRevenueSharePercent(Number(e.target.value))}
            />
          </Grid>
          {!isEdit && (
            <>
              <Grid item xs={6}>
                <TextField label="Логин" fullWidth value={login} onChange={(e) => setLogin(e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Пароль" type="password" fullWidth value={password} onChange={(e) => setPassword(e.target.value)} />
              </Grid>
            </>
          )}
        </Grid>

        {isEdit && rates && rates.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle2" mb={0.5}>
              Процент по каждой группе
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Если пусто — используется базовый процент ({formatPercent(defaultRevenueSharePercent)}). Процент считается от фактически поступивших оплат по группам этого преподавателя.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Группа</TableCell>
                  <TableCell>Предмет</TableCell>
                  <TableCell align="right">Учеников</TableCell>
                  <TableCell>Процент</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rates.map((rate) => (
                  <GroupRateRow key={rate.groupId} teacherId={teacher!.id} {...rate} />
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}>
          {isEdit ? "Сохранить" : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
